import {
	confirmInvestorKycForm,
	removeInvestorKycForm,
	resetInheritedKycItems,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import {
	noSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	FormPair,
	hasSubmission,
} from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import {
	getInheritedFormsDisplay,
	hasFormAwaitingRequiredSignature,
} from '@dealstack/frontend/investor/pages/Investment/common/InheritedFormsConfirmation/InheritedFormsConfirmation.utils';
import { useKycFormSigning } from '@dealstack/frontend/investor/pages/Investment/common/KycFormSigning/useKycFormSigning';

export const useJoinAndAllocationKycFormsController = () => {
	const tInvestor = useTranslationInvestor();

	const { setIsContinueDisabled, investmentDetails } = useContext(
		investmentJoinAndAllocationContext
	);

	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';

	const formsRequest = useGetInvestorKycForms(kycWorkflowId, { enabled: !!kycWorkflowId });
	const refreshData = formsRequest.refetch;

	const pairedForms = useMemo<FormPair[]>(
		() =>
			(formsRequest.data ?? []).map((form) => ({
				templateId: form.templateId,
				instanceId: form.instanceId,
				name: form.formName,
				instruction: form.instruction,
				templateFileId: form.templateFileId,
				fileId: form.fileId,
				fileName: form.fileName,
				status: form.status,
				isInherited: form.isInherited,
				subjectAction: form.subjectAction,
				rejectionComment: form.rejectionComment,
				createdAt: form.createdAt,
				requiresSignature: form.requiresSignature,
				signatureStatus: form.signatureStatus,
				signatureId: form.signatureId,
			})),
		[formsRequest.data]
	);

	const isFormsLoaded = !!formsRequest.data;
	const [inheritedDismissed, setInheritedDismissed] = useState(false);
	const { inheritedForms, isInheritedAndLocked, isConfirmedInherited } = useMemo(
		() => getInheritedFormsDisplay({ forms: pairedForms, inheritedDismissed }),
		[pairedForms, inheritedDismissed]
	);

	const onConfirmAllForms = useCallback(async () => {
		if (!inheritedForms.length) {
			return;
		}
		await Promise.all(
			inheritedForms.map((f) =>
				confirmInvestorKycForm({ templateId: f.templateId, kycWorkflowId })
			)
		);
		showToastV2({ title: tInvestor('investment.join.kyc.inherited.confirmed') });
		void refreshData();
	}, [inheritedForms, kycWorkflowId, refreshData, tInvestor]);

	const onDismissInheritedForms = useCallback(async () => {
		const allInherited = pairedForms.filter((f) => f.subjectAction != null);
		if (!allInherited.length) {
			setInheritedDismissed(true);
			return;
		}
		await Promise.all(
			allInherited.map((f) =>
				removeInvestorKycForm({ templateId: f.templateId, kycWorkflowId })
			)
		);
		showToastV2({ title: tInvestor('investment.join.kyc.kyc_forms.removed') });
		setInheritedDismissed(true);
		void refreshData();
	}, [pairedForms, kycWorkflowId, refreshData, tInvestor]);

	const onResetForms = useCallback(async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.FORM);
		setInheritedDismissed(false);
		void refreshData();
	}, [kycWorkflowId, refreshData]);

	useEffect(() => {
		if (isConfirmedInherited) {
			setIsContinueDisabled(false);
			return;
		}
		if (!pairedForms.length) {
			return;
		}
		if (isInheritedAndLocked) {
			setIsContinueDisabled(true);
			return;
		}
		const isAnyKycRejected = pairedForms.some((f) => isKycRejected(f.status));
		const allFilled = pairedForms.every((f) => hasSubmission(f));
		const hasUnconfirmed = pairedForms.some((f) => f.isInherited);
		// A submitted-but-unsigned form is `status: PENDING`, so `hasSubmission` alone would let the
		// investor Continue past it — and the join wizard offers no route back.
		const awaitsSignature = hasFormAwaitingRequiredSignature(pairedForms);
		setIsContinueDisabled(!allFilled || isAnyKycRejected || hasUnconfirmed || awaitsSignature);
	}, [pairedForms, isInheritedAndLocked, isConfirmedInherited, setIsContinueDisabled]);

	useWizardStep(noSave);

	const [showWetInkModal, setShowWetInkModal] = useState(false);
	const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
	const [selectedFormPair, setSelectedFormPair] = useState<FormPair>();

	const kycFormsWithInstructions = useMemo(
		() => pairedForms.filter((f) => !!f.instruction),
		[pairedForms]
	);
	const [selectedInstructionFormIndex, setSelectedInstructionFormIndex] = useState<number>();

	const onDelete = useCallback(
		async (templateId: string) => {
			await removeInvestorKycForm({ templateId, kycWorkflowId });
			showToastV2({ title: tInvestor('investment.join.kyc.kyc_forms.removed') });
			void refreshData();
		},
		[kycWorkflowId, refreshData, tInvestor]
	);

	const onPreview = useCallback((formPair: FormPair) => {
		setShowDocumentPreviewModal(true);
		setSelectedFormPair(formPair);
	}, []);

	const onSign = useCallback((formPair: FormPair) => {
		setShowWetInkModal(true);
		setSelectedFormPair(formPair);
	}, []);

	const onInstructionClick = useCallback(
		(templateId: string) => {
			const index = kycFormsWithInstructions.findIndex((f) => f.templateId === templateId);
			setSelectedInstructionFormIndex(index);
		},
		[kycFormsWithInstructions]
	);

	const { startSigningForm, signingModalProps } = useKycFormSigning({
		onSigned: refreshData,
		kycWorkflowId,
	});

	return {
		startSigningForm,
		signingModalProps,
		pairedForms,
		inheritedForms,
		isFormsLoaded,
		isInheritedAndLocked,
		isConfirmedInherited,
		onConfirmAllForms,
		onDismissInheritedForms,
		onResetForms,
		showWetInkModal,
		setShowWetInkModal,
		showDocumentPreviewModal,
		setShowDocumentPreviewModal,
		selectedFormPair,
		setSelectedFormPair,
		selectedInstructionFormIndex,
		setSelectedInstructionFormIndex,
		kycFormsWithInstructions,
		refreshData,
		onDelete,
		onPreview,
		onSign,
		onInstructionClick,
		kycWorkflowId,
	};
};
